import i18next from "i18next"

import api from "services/api"
import { dispatchLoggedOutEvent } from "./auth"
import dispatchMaintenanceMode from "./maintenance"
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { notification as toast } from "antd"
import {
  dispatchDevelopmentEvent,
  dispatchProductionEvent,
  dispatchStagingEvent,
} from "./banner/utils"
i18next.loadNamespaces("translation")

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess(_, __, ___, mutation) {
      const meta = mutation.options.meta
      let message: string = ""
      switch (true) {
        case !!meta?.message:
          message = meta?.message as string
          break
        case !!meta?.entity:
          message = i18next.t(`mutations.success.${meta?.type}`, {
            entity: meta?.entity,
            context: meta?.context,
            count: meta?.count as number,
          })
          break
      }
      if (message) {
        toast.success({ message: message, placement: "top" })
      }
    },
    onError(error: any, __, ___, mutation) {
      const meta = mutation.options.meta

      switch (true) {
        case meta?.handleError !== false && error.response.status === 400:
          toast.error({
            message: i18next.t<string>("error"),
            description: error.response.data.message,
            placement: "top",
          })
          break
        case meta?.handleError !== false && error.response.status !== 422:
          toast.error({
            message: i18next.t<string>("mutations.error.unexpected"),
            placement: "top",
          })
          break
        case error.response.status === 401:
          dispatchLoggedOutEvent()
          break
        case error.response.status === 503:
          dispatchMaintenanceMode()
      }
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: ({ signal, queryKey }) => {
        const params = queryKey
          .filter((key) => typeof key === "object")
          .reduce((acc: any, current: any) => ({ ...acc, ...current }), {})
        return api
          .get(
            queryKey
              .filter(
                (key) => typeof key === "string" || typeof key === "number"
              )
              .join("/"),
            {
              params,
              signal,
            }
          )
          .then((res) => {
            if (res.headers["api-environment"]) {
              if (res.headers["maintenance-mode"] === "1") {
                dispatchMaintenanceMode()
              } else {
                res.headers["api-environment"] === "production"
                  ? dispatchProductionEvent()
                  : res.headers["api-environment"] === "development"
                  ? dispatchDevelopmentEvent()
                  : dispatchStagingEvent()
              }
            }
            return res.data?.data
          })
      },
      retry: (failures, error: any) => {
        if (error.response.status === 404 || error.response.status === 503)
          return false
        return failures <= 3
      },
      onError(error: any) {
        if (error.response.status === 503) {
          dispatchMaintenanceMode()
        }
      },
      useErrorBoundary: (error: any) => {
        return error.response.status === 404
      },
      // notifyOnChangeProps: "tracked",
    },
  },
})

const QueryProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      {children}
    </QueryClientProvider>
  )
}

export default QueryProvider
